"use client"
import { type Trailer } from "@filmweb/sanity-types";
import FilmwebIdButtonPanel from "src/components/film/filmwebid/FilmwebIdButtonPanel";
import { MovieActivityWrapper } from "src/components/film/filmwebid/MovieActivityContext";
import LoadingIndicator from "src/components/loadingindicator/LoadingIndicator";
import CameraCircleSvg from "src/components/svg/CameraCircleSvg";
import PlaySvg from "src/components/svg/PlaySvg";
import theme from "src/components/themes/DefaultTheme";
import TrailerOverlayWrapper from "src/components/traileroverlaywrapper/TrailerOverlayWrapper";
import styled from 'src/lib/styles/css';
import IPremiumLandingPage from "src/lib/types/IPremiumLandingPage";
import dynamic from "next/dynamic";
import { Reducer, useReducer } from "react";

const DynamicImageGalleryDisplay = dynamic(() => import('src/components/imagegallery/ImageGalleryDisplay'), {
	loading: () => <LoadingIndicator />,
});

//#region [Props]
type ExtraProgramControlsProps = {
	landingpage: IPremiumLandingPage;
};
//#endregion

//#region [Component]
export default function ExtraProgramControls({ landingpage }: ExtraProgramControlsProps) {
	const [state, dispatch] = useReducer(reducer, { isShowingGallery: false, isShowingTrailer: false } as ReducerState)

	return <>
		<SExtraProgramControls>
			<MovieActivityWrapper edi={landingpage.movie.mainVersionEDI}>
				<SFilmwebIdButtonPanel edi={landingpage.movie.mainVersionEDI}
					hideUserInteractionStatus
					hideCommenting
					hideGiveRating
				/>

			</MovieActivityWrapper>
			{(landingpage.movie.trailers?.[0] as Trailer)?.videoId && <>

				<STrailerButton onClick={() => dispatch("showTrailer")}>
					<PlaySvg />
					<SIconText>Trailer</SIconText>
				</STrailerButton>
			</>}
			{(landingpage.movie.imagesV2?.length ?? 0) >= 2 && <SGalleryButton onClick={() => dispatch("showGallery")}>
				<SGallerySvg />
				<SIconText>Bilder</SIconText>
			</SGalleryButton>}

		</SExtraProgramControls>
		{state.isShowingTrailer && <TrailerOverlayWrapper onClose={() => dispatch("close")} videoId={Number((landingpage.movie.trailers[0] as Trailer)!.videoId)} hideRelated />}
		{state.isShowingGallery && <DynamicImageGalleryDisplay
			images={landingpage.movie.imagesV2}
			onClose={() => dispatch("close")} />}
	</>
}
//#endregion

type ReducerState = {
	isShowingTrailer: boolean;
	isShowingGallery: boolean;
}

const reducer: Reducer<ReducerState, "close" | "showGallery" | "showTrailer"> = (state, action) => {
	switch (action) {
		case "showGallery":
			return { ...state, isShowingGallery: true, isShowingTrailer: false };
		case "showTrailer":
			return { ...state, isShowingGallery: false, isShowingTrailer: true };
		case "close":
			return { ...state, isShowingGallery: false, isShowingTrailer: false };
		default:
			return state;
	}

}

//#region [Styles]
const SExtraProgramControls = styled.div`
	--TEXTCOLOR_OVERRIDE: var(--ADFREE-ACTIONBUTTONSCOLOR);
	--ICONCOLOR_OVERRIDE: var(--ADFREE-ACTIONBUTTONSCOLOR);

	order: 1;
	margin-left: auto;
	align-self: flex-end;
	width: 100%;
	margin-bottom: 25px;

	gap: 20px;
	display: flex;
	align-items: flex-start;
	justify-content: center;

	@media (min-width: 550px) {
        /*@media  (orientation: landscape) {*/
        order: 2;
        width: auto;
        margin-bottom: 0;
        margin-right: 2%;
    }
`;

const SFilmwebIdButtonPanel = styled(FilmwebIdButtonPanel)`
	width: auto;
	max-width: none;
	gap: 20px;
	@media (max-width: 549px) {
		margin-top: 0;

		.watchListButton, .favButton {
			align-self: flex-start;
			svg {
				height: 42px;
				width: auto;
			}
		}
	}

`;



const STrailerButton = styled.button`
	border: none;
	background: none;
	padding: 0;

	width: 42px;
	@media (min-width: 550px) {
		order: 2;

		margin-top: 25px;

	}
`;

const SGalleryButton = styled.button`
	border: none;
	background: none;
	padding: 0;
	width: 42px;
	@media (min-width: 550px) {
		order: 2;

		margin-top: 25px;
	}

`;

const SGallerySvg = styled(CameraCircleSvg)`
	width: 100%;
`;

const SIconText = styled.span`
	display: block;
	font-size: 14px;
	line-height: 22px;
	font-weight: normal;
	width: 100%;
	text-align: center;
	color: var(--TEXTCOLOR_OVERRIDE, var(--textcolor));

	@media (min-width: 550px) {
		line-height: 1;
	}
`;
//#endregion